export type AcceptedCredentialRoleFields = {
  role: string;
  roleId: string | null;
  roleDescription: string | null;
};

export type AcceptedCredentialAuthMaterialFields = {
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
};

export type AcceptedCredentialPendingEdits = {
  role?: AcceptedCredentialRoleFields;
  authMaterial?: AcceptedCredentialAuthMaterialFields;
};

export function mergeAcceptedCredentialPendingEditsById({
  roleByCredentialId,
  authMaterialByCredentialId,
}: {
  roleByCredentialId: Record<string, AcceptedCredentialRoleFields>;
  authMaterialByCredentialId: Record<
    string,
    AcceptedCredentialAuthMaterialFields
  >;
}): Record<string, AcceptedCredentialPendingEdits> {
  const mergedCredentialIds = new Set([
    ...Object.keys(roleByCredentialId),
    ...Object.keys(authMaterialByCredentialId),
  ]);

  return Object.fromEntries(
    [...mergedCredentialIds].map((credentialId) => [
      credentialId,
      {
        ...(roleByCredentialId[credentialId]
          ? { role: roleByCredentialId[credentialId] }
          : {}),
        ...(authMaterialByCredentialId[credentialId]
          ? { authMaterial: authMaterialByCredentialId[credentialId] }
          : {}),
      },
    ]),
  );
}

export function applyAcceptedCredentialPendingEdits<
  TCredential extends {
    id: string;
    role: string;
    roleId?: string | null;
    roleDescription?: string | null;
    headers: Array<{ key: string; value: string }>;
    cookies: Array<{ name: string; value: string }>;
  },
>({
  credentials,
  acceptedByCredentialId,
}: {
  credentials: TCredential[];
  acceptedByCredentialId: Record<string, AcceptedCredentialPendingEdits>;
}): TCredential[] {
  return credentials.map((entry) => {
    const accepted = acceptedByCredentialId[entry.id];
    if (!accepted) {
      return entry;
    }

    return {
      ...entry,
      ...(accepted.role
        ? {
            role: accepted.role.role,
            roleId: accepted.role.roleId,
            roleDescription: accepted.role.roleDescription,
          }
        : {}),
      ...(accepted.authMaterial
        ? {
            headers: accepted.authMaterial.headers,
            cookies: accepted.authMaterial.cookies,
          }
        : {}),
    };
  });
}
